import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { PageMeta } from '../../components/common/PageMeta'
import { bookingsApi } from '../../api/endpoints'
import { ApiError } from '../../api/client'
import { useFormatters } from '../../hooks/useFormatters'
import type { BookingStatus } from '../../types/api'

const STATUS_COLOR: Record<BookingStatus, 'warning' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'error',
  COMPLETED: 'default',
}

/**
 * Guest-facing booking lookup.
 *
 * Reference plus e-mail, deliberately. The reference alone would be guessable enough
 * (LS-2026-0007 is not a secret), and requiring the address the guest already typed keeps
 * the endpoint from leaking who is staying when.
 */
export function BookingLookupPage() {
  const { t } = useTranslation()
  const { dateLong, moneyPrecise } = useFormatters()
  const [reference, setReference] = useState('')
  const [email, setEmail] = useState('')

  const lookup = useMutation({
    mutationFn: () => bookingsApi.lookup(reference.trim(), email.trim()),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    lookup.mutate()
  }

  return (
    <Container sx={{ py: { xs: 6, md: 10 }, maxWidth: 560 }}>
      <PageMeta title={t('booking.lookupTitle')} path="/booking" noIndex />

      <Typography variant="h2" sx={{ mb: 1.5 }}>
        {t('booking.lookupTitle')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        {t('booking.lookupBody')}
      </Typography>

      <Paper
        component="form"
        onSubmit={submit}
        elevation={0}
        sx={{ p: 3, border: 1, borderColor: 'divider', borderRadius: 4 }}
      >
        <Stack spacing={2}>
          <TextField
            required
            label={t('booking.reference')}
            value={reference}
            onChange={(event) => setReference(event.target.value.toUpperCase())}
            placeholder="LS-2026-0007"
            slotProps={{ input: { sx: { fontVariantNumeric: 'tabular-nums' } } }}
          />
          <TextField
            required
            type="email"
            label={t('booking.email')}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
          />
          <Button type="submit" variant="contained" size="large" disabled={lookup.isPending}>
            {lookup.isPending ? t('common.loading') : t('booking.lookupCta')}
          </Button>
        </Stack>
      </Paper>

      {lookup.isError && (
        <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>
          {lookup.error instanceof ApiError && lookup.error.status === 404
            ? t('common.notFoundBody')
            : t('common.errorBody')}
        </Alert>
      )}

      {lookup.data && (
        <Paper
          elevation={0}
          sx={{ mt: 3, p: 3, border: 1, borderColor: 'divider', borderRadius: 4 }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h4" component="p">
              {lookup.data.reference}
            </Typography>
            <Chip
              color={STATUS_COLOR[lookup.data.status]}
              label={t(`booking.status${lookup.data.status}`)}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack spacing={1}>
            <Row label={t('houses.title')} value={lookup.data.unitName} />
            <Row label={t('booking.checkIn')} value={dateLong(lookup.data.checkIn)} />
            <Row label={t('booking.checkOut')} value={dateLong(lookup.data.checkOut)} />
            <Row
              label={t('booking.yourStay')}
              value={`${t('common.night', { count: lookup.data.nights })}, ${t('common.guest', {
                count: lookup.data.guests,
              })}`}
            />
            <Divider sx={{ my: 0.5 }} />
            <Row label={t('booking.total')} value={moneyPrecise(lookup.data.totalAmount)} strong />
          </Stack>
        </Paper>
      )}
    </Container>
  )
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500, textAlign: 'end' }}>
        {value}
      </Typography>
    </Box>
  )
}
